import { useTranslation } from 'react-i18next';
import type { Sample } from '../../api/endpoints.js';
import { ActionButton, Panel } from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { requestedTestName, requestedTestStandard } from './requested-tests.js';

/**
 * The tests requested for this sample (D215, D217, D220).
 *
 * TECHNICAL INFORMATION, NOT COMMERCIAL TERMS: each test's name in the
 * interface language and its reference standard, read from the work-order
 * service. No price, quantity or discount is shown, and none is sent.
 *
 * A sample with none reads "Not recorded" — every sample received before
 * requested tests were recorded looks like this — and NEVER lists the work
 * order's services as though they all applied to it.
 *
 * They are edited in the one Edit Sample dialog (D222), which the page opens
 * through `onEdit` — given only when the person may edit this sample. Without
 * a work order there is nothing to request (D216), so no button is shown.
 */
export function SampleRequestedTestsPanel({
  sample,
  onEdit,
}: {
  readonly sample: Sample;
  readonly onEdit?: () => void;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();

  return (
    <Panel
      title={t('sampleRequestedTests.title')}
      description={t('sampleRequestedTests.hint')}
      actions={
        onEdit && sample.workOrderId !== null ? (
          <ActionButton priority="secondary" size="sm" onPress={onEdit}>
            {t('sampleRequestedTests.edit')}
          </ActionButton>
        ) : null
      }
    >
      {sample.requestedServices.length === 0 ? (
        <div data-testid="requested-tests-not-recorded">
          <p className="text-body font-medium text-default-900">
            {t('sampleRequestedTests.notRecorded')}
          </p>
          <p className="mt-1 text-caption text-default-600">
            {t('sampleRequestedTests.notRecordedHint')}
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2" data-testid="requested-tests-list">
          {sample.requestedServices.map((service) => {
            const standard = requestedTestStandard(service.referenceStandard, service.standardVersion);
            return (
              <li
                key={service.workOrderServiceId}
                className="min-w-0 rounded-small border border-default-200 bg-content1 px-3 py-2"
              >
                <p className="text-body text-default-900">
                  {requestedTestName(service.descriptionAr, service.descriptionEn, locale) ??
                    t('common.notRecorded')}
                </p>
                {standard ? (
                  <p className="mt-0.5 text-caption text-default-700" dir="ltr">
                    {standard}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

